import { documentTitleFromBooking } from "@albertine/design-system/utils/shareDocumentTitle";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Lib, useAlbertineTranslation } from "albertine-shared-web";
import { useRef } from "react";
import { BackButton } from "@/routes/-components/BackButton";
import { BookingContent } from "@/routes/-components/booking/BookingContent";
import { BookingHero } from "@/routes/-components/booking/BookingHero";
import {
    BOOKING_FOOTER_BACKGROUND,
    BOOKING_HERO_BACKGROUND,
    BOOKING_REVEAL_BACKGROUND_CLASS,
    BookingPageLayout,
} from "@/routes/-components/booking/BookingPageLayout";
import { CallToAction } from "@/routes/-components/CallToAction";
import { useShareLink } from "@/routes/-hooks/useShareLink";
import { useSkeletonRevealPage } from "@/routes/-hooks/useSkeletonRevealPage";
import { SharedBookingPage } from "./-components/SharedBookingPage";
import {
    BookingProvider,
    useBooking,
    useIsBookingLoaded,
} from "./-context/BookingContext";
import { SharedBookingProvider } from "./-context/SharedBookingContext";
import { useBookingActions } from "./-hooks/useBookingActions";
import { memberBookingToPublicBooking } from "./-utils/bookingDataMapper";
import {
    bookingSearchSchema,
    isAgentPreviewSearch,
    isPublicShareSearch,
} from "./-utils/bookingSearchSchema";

export const Route = createFileRoute("/booking/")({
    validateSearch: bookingSearchSchema,
    component: RouteComponent,
    head: () => ({ meta: [{ title: "Booking" }] }),

    beforeLoad: ({ search }) => {
        const isSkeletonRevealed = !(
            isPublicShareSearch(search) || isAgentPreviewSearch(search)
        );
        document.documentElement.style.setProperty(
            "--color-body-top",
            BOOKING_HERO_BACKGROUND,
        );
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            isSkeletonRevealed ? BOOKING_HERO_BACKGROUND : BOOKING_FOOTER_BACKGROUND,
        );
    },

    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const search = Route.useSearch();

    if (isPublicShareSearch(search) || isAgentPreviewSearch(search)) {
        return (
            <SharedBookingProvider>
                <SharedBookingPage />
            </SharedBookingProvider>
        );
    }

    return (
        <BookingProvider>
            <BookingPage />
        </BookingProvider>
    );
}

function BookingPage() {
    const memberBooking = useBooking();
    const isLoaded = useIsBookingLoaded();
    const reveal = useSkeletonRevealPage({
        isLoaded,
        loadingBodyColor: BOOKING_HERO_BACKGROUND,
        revealedBodyColor: BOOKING_FOOTER_BACKGROUND,
    });
    const { tenant, locale } = Route.useSearch();
    const { openChat } = useBookingActions(memberBooking, tenant, locale);
    const t = useAlbertineTranslation();
    const footerRef = useRef<HTMLDivElement>(null);
    const { share, isPending: isSharePending } = useShareLink({
        resourceType: "booking",
        resourceId: memberBooking.id,
        title: memberBooking.title ?? "",
    });

    const publicBooking = memberBookingToPublicBooking(memberBooking);

    return (
        <>
            <BookingPageLayout
                id={publicBooking.id}
                tenant={tenant}
                title={publicBooking.title}
                documentTitle={documentTitleFromBooking(publicBooking)}
                hero={<BookingHero.Booking booking={publicBooking} />}
                backButton={<BackButton />}
                shareAction={{ onShare: share, isPending: isSharePending }}
                reveal={reveal}
                footer={
                    <div ref={footerRef}>
                        <CallToAction
                            openChat={openChat}
                            className={BOOKING_REVEAL_BACKGROUND_CLASS}
                        />
                    </div>
                }
            >
                <BookingContent.Booking
                    booking={publicBooking}
                    bookingReference={memberBooking.reference}
                    isLoaded={isLoaded}
                />
            </BookingPageLayout>

            {/* Waits for the on-screen reveal, so its entrance animation doesn't compete with the blur. */}
            {reveal.isVisibleContentRevealed && (
                <Lib.FloatingCTAButton
                    hideTriggerRef={footerRef}
                    insideContentArea={false}
                    opacityOnly
                    onClick={openChat}
                >
                    {t("call-to-action__button")}
                </Lib.FloatingCTAButton>
            )}
        </>
    );
}
